/*
Copyright (C) 2018 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { FC } from 'react';

import {
  useImpersonateUser,
  useHasAccess,
  useAuthContext
} from '@broadleaf/admin-components/dist/authentication/hooks';
import SVG from '@broadleaf/admin-components/dist/common/components/SVG';
import Button from '@broadleaf/admin-components/dist/common/elements/Button';
import { compile } from '@broadleaf/admin-components/dist/common/utils/template';
import { useCurrentApplication } from '@broadleaf/admin-components/dist/tracking/hooks';
import type { IMetadata } from '@broadleaf/admin-components/dist/types/metadata';
import { get } from 'lodash';

export interface ActionListGridImpersonateActionProps {
  /** The action definition. */
  actionDefinition?: IMetadata & {
    attributes?: {
      /** The name of the SVG icon. */
      iconName?: string;
      /** The name of the SVG icon. */
      iconColor?: string;
      /**
       * Whether this action is for allowing the CSR to access the storefront as
       * themselves rather than as another customer or anonymously
       */
      impersonateSelf?: boolean;
      /** Path to the id of the row item. Default is `id` */
      rowItemId?: string;
    };
  };
  /** Any context parameters passed through to the action. */
  contextParams?: object;
  /** Whether or not the grid is disabled. */
  disabled?: boolean;
  /** Whether or not the grid is loading. */
  loading?: boolean;
  /** The current row. */
  row?: object;
}

export const ActionListGridImpersonateAction: FC<ActionListGridImpersonateActionProps> = props => {
  const { actionDefinition, disabled, row } = props;
  const {
    attributes: {
      iconName = 'store-front',
      iconColor = 'gray',
      impersonateSelf = false,
      redirectContextPath
    },
    label
  } = actionDefinition;
  const application = useCurrentApplication();
  const compiledRedirectContextPath =
    redirectContextPath && (compile(redirectContextPath)(row) as string);
  const { user } = useAuthContext();

  const username = impersonateSelf
    ? user.username
    : get(row, 'customerRef.username');
  const impersonateUser = useImpersonateUser({
    username,
    impersonateSelf,
    redirectContextPath: compiledRedirectContextPath
  });

  const hasAccess = useHasAccess(
    actionDefinition.scope,
    actionDefinition.operationType
  );

  // return nothing if not within the context of an application
  if (!application || !hasAccess) {
    return null;
  }

  return (
    <Button
      className={`tw-ml-2 tw-text-${iconColor}-500 hover:tw-text-${iconColor}-700 active:tw-text-${iconColor}-900`}
      color={'transparent'}
      disabled={disabled}
      flat
      narrow
      onClick={impersonateUser}
      size="sm"
      title={label}
      type="button"
    >
      <SVG className="tw-h-4 tw-w-4 tw-fill-current" name={iconName} />
    </Button>
  );
};

export default ActionListGridImpersonateAction;
